import React, { useState, useEffect } from 'react'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import {
  Autocomplete,
  TextField,
  FormControl,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import { useAuthContext } from '../../../hooks/useAuthContext'
import previousActivityService from '../../../services/admin/previousActivity'

const PreviousActivityDialogueBox = ({ processes, open, onClose }) => {
  const [process, setProcess] = useState(null)
  const [activities, setActivities] = useState(null)
  const [loading, setLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState(null)
  const { user } = useAuthContext()

  useEffect(() => {
    if (open) {
      setProcess(null)
      setActivities(null)
      setLoading(false)
      setConfirmOpen(false)
      setSelectedActivity(null)
    }
  }, [open])

  const handleProcessChange = async (event, selectedValue) => {
    setProcess(selectedValue)
    if (!selectedValue) {
      setActivities(null)
      return
    }
    try {
      setLoading(true)
      const data = await previousActivityService.getPreviousActivities(user, selectedValue.id)
      setActivities(data)
    } catch (error) {
      console.error('Error fetching activities:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleActivityClick = (activity) => {
    setSelectedActivity(activity)
    setConfirmOpen(true)
  }

  const handleConfirmClose = () => {
    setConfirmOpen(false)
    setSelectedActivity(null)
  }

  const handleConfirm = async () => {
    try {
      await previousActivityService.revertActivity(
        user,
        process.id,
        selectedActivity.process_reference_num
      )
      // Refresh activities list
      const data = await previousActivityService.getPreviousActivities(user, process.id)
      setActivities(data)
      handleConfirmClose()
    } catch (error) {
      console.error('Error reverting activity:', error)
      alert('Failed to revert activity')
    }
  }

  return (
    <>
      <DialogWrapper heading={'Previous Activities'} open={open} onClose={onClose}>
        <div style={{ padding: '6px 0px 0px 0px' }}>
          <FormControl variant="outlined" fullWidth size="small" color="primary">
            <Autocomplete
              fullWidth
              style={{ minWidth: 240 }}
              id="select-derived-field"
              options={processes}
              size="small"
              getOptionLabel={(item) => item?.process_template_name}
              value={process || null}
              onChange={handleProcessChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Processes"
                  color="primary"
                />
              )}
            />
            <Box
              sx={{
                mt: 2,
                maxHeight: 240,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
              }}
            >
              {loading && (
                <Typography variant="body2" sx={{ p: 1 }} color="text.secondary">
                  Loading...
                </Typography>
              )}
              {!loading && (!activities || activities.length === 0) && (
                <Typography variant="body2" sx={{ p: 1 }} color="text.secondary">
                  No activities found
                </Typography>
              )}
              {!loading &&
                activities?.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 1,
                      borderBottom: '1px solid #f5f5f5',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f5f5f5' },
                    }}
                    onClick={() => handleActivityClick(item)}
                  >
                    <Typography variant="body2">
                      {item.prefix}-{item.process_reference_num} ({item.activity_type})
                    </Typography>
                  </Box>
                ))}
            </Box>
          </FormControl>
        </div>
      </DialogWrapper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleConfirmClose}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to put back{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              "{selectedActivity?.prefix}-{selectedActivity?.process_reference_num} (
              {selectedActivity?.activity_type})"
            </Box>{' '}
            to previous activity ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PreviousActivityDialogueBox
